/**
 * Production hierarchy-by-URL loader — INIT-2026-Q3-SCENE-HIERARCHY close-out C2.
 *
 * Large object indexes are delivered by reference (`SceneItem.hierarchyUrl`)
 * rather than inline (G1). This module fetches a converter-produced
 * `object-index.json` from that URL and converts it into a
 * `SceneHierarchyManifest` the Scene Tree V2 can render.
 *
 * It is the production-safe counterpart of the DEV console helper
 * `window.__sceneHierarchy.loadObjectIndex(...)`: it never throws (returns
 * `null` on any failure) and only logs in DEV builds.
 */

import { objectIndexToManifest } from "@/components/scene-tree/objectIndexToManifest";
import type { ObjectIndex } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { SceneHierarchyManifest } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";

function devError(message: string, err?: unknown): void {
  if (import.meta.env.DEV) {
    console.error(`[scene-hierarchy] ${message}`, err ?? "");
  }
}

/**
 * Fetch and convert an `object-index.json` into a manifest.
 *
 * @param urlOrPath   - Absolute URL or app-relative path to the index JSON.
 * @param fetchImpl   - Injectable fetch (defaults to global `fetch`); enables testing.
 * @returns The converted manifest, or `null` if the fetch/parse/convert failed.
 */
export async function fetchObjectIndexManifest(
  urlOrPath: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SceneHierarchyManifest | null> {
  if (!urlOrPath) return null;

  let raw: unknown;
  try {
    const response = await fetchImpl(urlOrPath);
    if (!response.ok) {
      devError(
        `hierarchyUrl fetch failed for "${urlOrPath}" — HTTP ${response.status} ${response.statusText}`,
      );
      return null;
    }
    raw = await response.json();
  } catch (err) {
    devError(`hierarchyUrl fetch/parse failed for "${urlOrPath}"`, err);
    return null;
  }

  try {
    // Directory of the index URL — used to resolve a paginated index's
    // relative child-segment URLs (node.metadata.hierarchyUrl).
    const lastSlash = urlOrPath.lastIndexOf("/");
    const segmentBaseUrl = lastSlash >= 0 ? urlOrPath.slice(0, lastSlash + 1) : "";
    return objectIndexToManifest(raw as ObjectIndex, { segmentBaseUrl });
  } catch (err) {
    devError(`hierarchyUrl conversion failed for "${urlOrPath}"`, err);
    return null;
  }
}
